/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getTiposDeEmpleados } from 'api/catalogos/tiposDeEmpleados';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getTiposAsignacion,
  postTipoAsignacion,
  deleteTipoAsignacion,
  patchTipoAsignacion,
  getTipoAsignacion,
} from 'api/catalogos/TipoAsignacionDePlaza';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';
import { NOMBRES_PATTERN_GENERICO } from 'utils/patterns';

export const tipodeasignaciondeplazaPermission = {
  permissionModel: 'tipodeasignaciondeplaza',
  catalogsModels: [],
  tabsModels: [],
};

const TipoAsignacionPlaza = ({ permission }) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [, setTiposEmpleados] = useState([]);
  const [estados, setEstados] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);

  const formInstances = [
    form,
    formDatos,
  ];

  const baseURI = 'catalogos/categoria-de-puesto';

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposAsignacion();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tiposEmpleados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeEmpleados(),
        getTiposAsignacion(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTiposEmpleados(_tiposEmpleados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setDisableForm(false);
    setSelectedRowKeys([]);
    setVisible(false);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Autorizar un Sindicato ya Autorizado');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchTipoAsignacion(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableForm(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _uma = await getTipoAsignacion(match.id);
    setTimeout(() => {
      if (_uma) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          isDisabled(_uma?.estados_globales);
          form.setFieldsValue(formatReceived(_uma));
          formDatos.setFieldsValue(formatReceived(_uma));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchTipoAsignacion(key, values) : postTipoAsignacion(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTipoAsignacion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDeleteDisabled(isAutorizado);
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    form.resetFields();
    formDatos.resetFields();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 400,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN_GENERICO,
        message: 'Ingrese un nombre válido',
      },
    ],
    empleado: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          titleTable="Categoría de Empleados"
          allowImport
          baseURI={baseURI}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          customActions={[
            {
              onClick: () => setVisibleAuthConfirmation(true),
              permission: { model: 'tipodeasignaciondeplaza', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys?.length || disableAuth,
            },
          ]}
          disableDelete={
            deleteDisabled
          }
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Categoría de Empleados`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
            )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            disabled={disableForm}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="nombre"
                rules={rules.nombre}
                label="Nombre"
                hasFeedback
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  activeInactive={!deleteDisabled}
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                  onChange={(val) => {
                    if (val === ESTATUS_INACTIVO) {
                      setVisibleAlertInactive(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Categoría de Puesto ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Inactivar la Categoría de Puesto ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Autorizar la Categoría de Puesto ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TipoAsignacionPlaza;
